import {
    IonButton,
    IonCard,
    IonCardContent,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonListHeader,
    IonModal,
    IonRefresher,
    IonRefresherContent,
    RefresherEventDetail,
    useIonToast,
} from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import { eyeOffOutline, eyeOutline, lockClosedOutline } from 'ionicons/icons';
import React, {
    MouseEventHandler,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import LoadableContent from '../../../components/content/LoadableContent';
import LoadingButton from '../../../components/loadingbutton/LoadingButton';
import InterventionPage from '../../../components/page/InterventionPage';
import { IInstallationRouteProps } from '../../../interfaces/IRoute';
import { ESPConstants } from '../../../provisioning/ESPConstants';
import { ESPProvisionManager } from '../../../provisioning/ESPProvisionManager';
import { WiFiAccessPoint } from '../../../provisioning/WiFiAccessPoint';
import { trackActionsRequest } from '../../../store/Requests';
import wifiFairSvg from '../../../theme/icons/wifi-fair.svg';
import wifiSlashSvg from '../../../theme/icons/wifi-slash.svg';
import wifiWeakSvg from '../../../theme/icons/wifi-weak.svg';
import wifiSvg from '../../../theme/icons/wifi.svg';
import { LTE_CREDENTIALS } from '../../../utils/constants';
import { useCustomRouter } from '../../../utils/hooks/useCustomRouter';

const SelectNetwork: React.FC<IInstallationRouteProps> = ({ match }) => {
    const provisionManager = ESPProvisionManager.getInstance();
    const router = useCustomRouter();
    const jobId = +match.params.jobId;
    const [presentToast] = useIonToast();
    const modal = useRef<HTMLIonModalElement>(null);
    const macAddressInput = useRef<HTMLIonInputElement>(null);
    const ssidInput = useRef<HTMLIonInputElement>(null);
    const passwordInput = useRef<HTMLIonInputElement>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [wifiList, setWifiList] = useState<WiFiAccessPoint[]>([]);
    const [selectedSsid, setSelectedSsid] = useState('');
    const [openNetwork, setOpenNetwork] = useState(false);
    const [networkType, setNetworkType] = useState<
        'visible' | 'hidden' | 'lte' | undefined
    >(undefined);
    const connectedMacAddress = provisionManager
        .getEspDevice()
        .getDeviceMacAddress();

    const onSelectVisibleNetworkHandler: MouseEventHandler<HTMLIonItemElement> =
        useCallback((e) => {
            const target = e.currentTarget as HTMLIonItemElement;
            const ssid = target.dataset.ssid as string;
            const security = target.dataset.security as string;
            setSelectedSsid(ssid);
            setOpenNetwork(+security === ESPConstants.WIFI_OPEN);
            setNetworkType('visible');
        }, []);

    const onSelectHiddenNetworkHandler = useCallback(() => {
        setSelectedSsid('');
        setNetworkType('hidden');
    }, []);

    const onSelectLteNetworkHandler = useCallback(() => {
        setSelectedSsid('');
        setNetworkType('lte');
    }, []);

    const onAlertConfirmHandler = useCallback(async () => {
        setLoading(true);
        try {
            // We need to call the scanNetworks method again because if we pass
            // wrong credentials to the device and the good credentials Espressif
            // will say credentials already provided. This is a workaround to
            // refresh the list of networks sot that the user can try again.
            await provisionManager.getEspDevice().scanNetworks();

            if (
                networkType === 'lte' &&
                macAddressInput.current!.value!.toString().toLowerCase() !==
                    connectedMacAddress.toLowerCase()
            ) {
                throw new Error(
                    t`You must enter the mac address of the device`
                );
            }

            const ssid =
                networkType === 'visible'
                    ? selectedSsid
                    : networkType === 'lte'
                      ? LTE_CREDENTIALS
                      : ssidInput.current!.value!.toString();
            const password =
                networkType === 'lte'
                    ? LTE_CREDENTIALS
                    : openNetwork
                      ? ''
                      : passwordInput.current!.value!.toString();

            if (!ssid) {
                throw new Error(t`You must enter the network name`);
            }

            if (!openNetwork && !password) {
                throw new Error(t`You must enter the password of the network`);
            }

            trackActionsRequest('save_wifi_credentials');

            const details = { ssid, password };
            const deviceState = await provisionManager
                .getEspDevice()
                .getDeviceState();

            await provisionManager
                .getEspDevice()
                .provision(details.ssid, details.password);
            trackActionsRequest('connected_device_provisioned');

            setNetworkType(undefined);
            setOpenNetwork(false);
            setSelectedSsid('');

            if (deviceState === ESPConstants.ProvisionState.IDLE_NEW) {
                router.push(
                    `/${jobId}/installation/mesh/provisioning-macs`,
                    'forward'
                );
            } else {
                router.push(
                    `/${jobId}/installation/mesh/maintenance`,
                    'back',
                    'pop'
                );
            }
        } catch (e) {
            const error = e as Error;
            presentToast({
                message: error.message,
                duration: 5000,
                color: 'danger',
                buttons: [
                    {
                        text: t`Dismiss`,
                        role: 'cancel',
                    },
                ],
            });
        }
        setLoading(false);
    }, [
        connectedMacAddress,
        jobId,
        networkType,
        openNetwork,
        presentToast,
        provisionManager,
        router,
        selectedSsid,
    ]);

    const handleRefresh = useCallback(
        async (event: CustomEvent<RefresherEventDetail>) => {
            setHasError(false);
            try {
                const wifiList = await provisionManager
                    .getEspDevice()
                    .scanNetworks();
                setWifiList(wifiList);
            } catch (e) {
                const error = e as Error;
                presentToast({
                    message: error.message,
                    duration: 5000,
                    color: 'danger',
                    buttons: [
                        {
                            text: t`Dismiss`,
                            role: 'cancel',
                        },
                    ],
                });
            }
            event.detail.complete();
        },
        [presentToast, provisionManager]
    );

    const getRssiLevelIcon = useCallback((rssi: number | undefined) => {
        if (!rssi) {
            return wifiSlashSvg;
        }
        if (rssi <= -80) {
            return wifiWeakSvg;
        }
        if (rssi <= -70) {
            return wifiFairSvg;
        }
        return wifiSvg;
    }, []);

    useEffect(() => {
        const init = async () => {
            setHasError(false);
            setIsLoading(true);
            try {
                const wifiList = await provisionManager
                    .getEspDevice()
                    .scanNetworks();
                setWifiList(wifiList);
            } catch (e) {
                setHasError(true);
            }
            setIsLoading(false);
        };

        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <InterventionPage title={t`Mesh`}>
            <LoadableContent
                ignoreError
                value={{ state: isLoading ? 'loading' : 'hasData' }}
            >
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent refreshingSpinner="circular"></IonRefresherContent>
                </IonRefresher>

                {hasError ? (
                    <IonCard color="warning" className="ion-margin">
                        <IonCardContent>
                            <Trans>Oops! Something went wrong!</Trans>
                            <p>
                                <Trans>Pull down to retry.</Trans>
                            </p>
                        </IonCardContent>
                    </IonCard>
                ) : (
                    <>
                        <IonList>
                            <IonListHeader color="primary">
                                <IonLabel>
                                    <Trans>Select Network</Trans>
                                </IonLabel>
                            </IonListHeader>
                            {wifiList.map((wifi) => {
                                return (
                                    <IonItem
                                        key={wifi.getWifiName()}
                                        data-ssid={wifi.getWifiName()}
                                        data-security={wifi.getSecurity()}
                                        onClick={onSelectVisibleNetworkHandler}
                                    >
                                        {wifi.getSecurity() !==
                                            ESPConstants.WIFI_OPEN && (
                                            <IonIcon
                                                slot="end"
                                                icon={lockClosedOutline}
                                            ></IonIcon>
                                        )}
                                        <IonIcon
                                            slot="end"
                                            src={getRssiLevelIcon(
                                                wifi.getRssi()
                                            )}
                                        ></IonIcon>
                                        <IonLabel>
                                            {wifi.getWifiName()}
                                        </IonLabel>
                                    </IonItem>
                                );
                            })}
                            <IonItem
                                key="joinOtherNetwork"
                                onClick={onSelectHiddenNetworkHandler}
                            >
                                <IonLabel>
                                    <Trans>Join Other Network</Trans>
                                </IonLabel>
                            </IonItem>
                            {/* Make this conditional for FW version 1.1.1 and up */}
                            <IonItem
                                key="useLte"
                                onClick={onSelectLteNetworkHandler}
                            >
                                <IonLabel>
                                    <Trans>I don't have a WiFi Network</Trans>
                                </IonLabel>
                            </IonItem>
                        </IonList>

                        <IonModal
                            className="alert-modal"
                            ref={modal}
                            isOpen={!!networkType}
                            backdropDismiss={false}
                            onDidDismiss={() => {
                                setShowPassword(false);
                                setNetworkType(undefined);
                            }}
                        >
                            <div className="wrapper">
                                {networkType !== 'lte' && (
                                    <h1>
                                        {networkType === 'hidden'
                                            ? t`Join Other Network`
                                            : openNetwork
                                              ? t`Connect to ${selectedSsid}`
                                              : selectedSsid}
                                    </h1>
                                )}
                                {networkType === 'lte' && (
                                    <>
                                        <p>
                                            <Trans>
                                                We advise you to use Wi-Fi for
                                                the best experience. However,
                                                if you don't have a Home
                                                Network you can select LTE.
                                            </Trans>
                                        </p>
                                        <p>
                                            <Trans>
                                                You need to enter the mac
                                                address of the device you are
                                                connected to in order to use
                                                LTE. You can find it on the
                                                back of the device
                                            </Trans>
                                        </p>
                                    </>
                                )}

                                {networkType === 'hidden' && (
                                    <IonItem>
                                        <IonInput
                                            ref={ssidInput}
                                            name="ssid"
                                            placeholder={t`Network Name`}
                                        ></IonInput>
                                    </IonItem>
                                )}
                                {(networkType === 'hidden' ||
                                    (networkType === 'visible' &&
                                        !openNetwork)) && (
                                    <IonItem>
                                        <IonInput
                                            ref={passwordInput}
                                            name="ssidPassword"
                                            placeholder={t`Password`}
                                            clearOnEdit={false}
                                            type={
                                                showPassword
                                                    ? 'text'
                                                    : 'password'
                                            }
                                            autocomplete="new-password"
                                        ></IonInput>
                                        <IonButton
                                            className="show-password"
                                            fill="clear"
                                            onClick={() => {
                                                setShowPassword(
                                                    (value) => !value
                                                );
                                            }}
                                        >
                                            <IonIcon
                                                slot="icon-only"
                                                icon={
                                                    showPassword
                                                        ? eyeOffOutline
                                                        : eyeOutline
                                                }
                                            />
                                        </IonButton>
                                    </IonItem>
                                )}
                                {networkType === 'lte' && (
                                    <IonItem>
                                        <IonInput
                                            ref={macAddressInput}
                                            name="macAddress"
                                            placeholder={t`Mac Address`}
                                            type="text"
                                        ></IonInput>
                                    </IonItem>
                                )}
                            </div>
                            <div className="actions">
                                <IonButton
                                    className="cancel"
                                    fill="clear"
                                    onClick={() => {
                                        modal.current?.dismiss();
                                    }}
                                >
                                    <Trans>Cancel</Trans>
                                </IonButton>
                                <LoadingButton
                                    loading={loading}
                                    expand={undefined}
                                    onClick={onAlertConfirmHandler}
                                >
                                    <Trans>Connect</Trans>
                                </LoadingButton>
                            </div>
                        </IonModal>
                    </>
                )}
            </LoadableContent>
        </InterventionPage>
    );
};

export default SelectNetwork;
